import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { SectionHeading } from "@/components/SectionHeading";
import { images, values } from "@/data/content";

const About = () => (
  <>
    <SEO
      title="Meet Alicia Joy | Bello Joy Consulting"
      description="Learn about Alicia Joy, a compassionate insurance professional with 25 years of experience serving families and businesses."
    />
    <PageHero
      testId="about-page-hero"
      kicker="Meet Alicia Joy"
      title="A trusted advocate"
      accent="with a caring heart."
      description="For 25 years, Alicia has helped individuals, families, and businesses make confident decisions through honest guidance, personalized attention, and genuine care."
      image={images.alicia}
      imageAlt="A warm professional advisor smiling in an office"
      imageCaption="Professional stock imagery shown until Alicia’s official portrait is available."
    />

    <section data-testid="alicia-story-section" className="px-6 pb-24 md:pb-32">
      <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <SectionHeading
            testId="alicia-story-heading"
            kicker="Her mission"
            title="Building lifelong relationships, not transactions."
            description="Alicia’s work is rooted in the belief that every client deserves to be heard, respected, and guided with integrity. She takes time to understand people’s stories, goals, concerns, and dreams before recommending solutions."
          />
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="alicia-story-copy">
            <p>
              As a dedicated insurance professional, Alicia combines deep industry knowledge with a caring heart. She makes insurance easier to understand and less overwhelming, helping families prepare for the unexpected while building a secure future.
            </p>
            <p>
              Clients know her for integrity, reliability, and a genuine desire to serve others. Her mission is not simply selling insurance products—it is helping people protect the blessings they have worked hard to create.
            </p>
          </div>
        </div>
        <Reveal className="rounded-[2rem] bg-primary p-8 text-primary-foreground shadow-soft md:p-10 dark-grain relative overflow-hidden" data-testid="alicia-credentials-card">
          <div className="relative z-10">
            <p className="font-serif text-7xl italic text-secondary">25</p>
            <p className="mt-2 text-sm font-semibold uppercase tracking-[0.24em] text-primary-foreground/75">Years in insurance & financial services</p>
            <div className="mt-10 space-y-5">
              {[
                "Compassionate advisor and trusted advocate",
                "Education-first, pressure-free guidance",
                "Faith, family, and customer-first values",
                "Personalized strategies for every client",
              ].map((item, index) => (
                <div key={item} data-testid={`alicia-credential-${index + 1}`} className="flex items-start gap-4 border-t border-primary-foreground/15 pt-5">
                  <CheckCircle2 className="mt-1 shrink-0 text-secondary" size={21} />
                  <p className="text-primary-foreground/82">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section data-testid="about-values-section" className="border-y border-border bg-muted/50 px-6 py-24 md:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          testId="about-values-heading"
          kicker="Core values"
          title="The principles behind every recommendation."
          align="center"
        />
        <div className="grid gap-6 md:grid-cols-3" data-testid="about-values-grid">
          {values.map((value, index) => (
            <Reveal key={value.title} delay={index * 0.08} className="rounded-[2rem] border border-border bg-card p-8 text-center shadow-soft" data-testid={`about-value-card-${index + 1}`}>
              <span className="font-serif text-5xl italic text-secondary">0{index + 1}</span>
              <h3 className="mt-5 text-2xl text-primary">{value.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{value.description}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="about-cta" className="px-6 py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
        <Reveal className="image-frame overflow-hidden rounded-[2rem] shadow-soft" data-testid="about-meeting-image">
          <img src={images.meeting} alt="A warm consultation between advisor and clients" className="aspect-[4/3] w-full object-cover" loading="lazy" />
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-secondary">A personal invitation</p>
          <h2 className="text-balance text-4xl leading-tight text-primary md:text-6xl">Begin a conversation with Alicia.</h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
            Whether you are reviewing existing coverage, preparing for a new season, or simply asking questions, you will be met with warmth, patience, and respect.
          </p>
          <Button asChild size="lg" className="mt-8 rounded-full bg-primary px-8 text-primary-foreground transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-primary/90">
            <Link to="/contact" data-testid="about-contact-button">Schedule a Consultation <ArrowRight size={17} /></Link>
          </Button>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
